import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import AppLayout from '../components/AppLayout';
import { routes } from '../lib/routes';
import './NovoProtocolo.css';

const tipoProcessoMap = {
  aprovacao_obra: 1,
  regularizacao: 2,
  reforma: 3,
  habite_se: 4,
};

const TIPO_OPTIONS = [
  { value: 'aprovacao_obra', label: 'Aprovação de obra' },
  { value: 'regularizacao', label: 'Regularização' },
  { value: 'reforma', label: 'Reforma' },
  { value: 'habite_se', label: 'Habite-se' },
];

export default function NovoProtocolo() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState({
    tipo: 'aprovacao_obra',
    endereco_obra: {
      logradouro: '',
      numero: '',
      bairro: '',
      cep: '',
      cidade_uf: '',
    },
    descricao: '',
    area_terreno: '',
    area_construida: '',
  });

  const setAddr = (key, value) => {
    setForm((prev) => ({
      ...prev,
      endereco_obra: { ...prev.endereco_obra, [key]: value },
    }));
  };

  const mutation = useMutation(
    () =>
      api
        .post('/protocolos', {
          ...form,
          tipo: form.tipo,
          processo_tipo_id: tipoProcessoMap[form.tipo] || 1,
          area_terreno: parseFloat(String(form.area_terreno || '0'), 10) || 0,
          area_construida: parseFloat(String(form.area_construida || '0'), 10) || 0,
        })
        .then((r) => r.data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('protocolos');
        toast.success('Protocolo criado com sucesso.');
        navigate(routes.protocolo(data.id), { replace: true });
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Erro ao criar protocolo.');
      },
    }
  );

  const addr = form.endereco_obra;

  return (
    <AppLayout title="Novo protocolo" subtitle="Preencha os dados principais para abertura.">
      <div className="novo-protocolo-page">
        <div className="panel novo-protocolo-panel">
          <div className="novo-protocolo-head">
            <h2>Formulário de abertura</h2>
          </div>

          <div className="novo-protocolo-body">
            <section className="novo-protocolo-section" aria-labelledby="np-sec-tipo">
              <h3 className="novo-protocolo-section-title" id="np-sec-tipo">
                Processo
              </h3>
              <div className="novo-protocolo-grid novo-protocolo-grid--1">
                <div className="novo-protocolo-field">
                  <label htmlFor="np-tipo">Tipo de processo</label>
                  <select
                    id="np-tipo"
                    value={form.tipo}
                    onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                  >
                    {TIPO_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

            <section className="novo-protocolo-section" aria-labelledby="np-sec-end">
              <h3 className="novo-protocolo-section-title" id="np-sec-end">
                Endereço da obra
              </h3>
              <div className="novo-protocolo-grid novo-protocolo-grid--1">
                <div className="novo-protocolo-field novo-protocolo-field--full">
                  <label htmlFor="np-log">Logradouro</label>
                  <input
                    id="np-log"
                    type="text"
                    placeholder="Rua, avenida, travessa…"
                    value={addr.logradouro || ''}
                    onChange={(e) => setAddr('logradouro', e.target.value)}
                    autoComplete="street-address"
                  />
                </div>
              </div>
              <div className="novo-protocolo-grid novo-protocolo-grid--4 novo-protocolo-address-row">
                <div className="novo-protocolo-field">
                  <label htmlFor="np-num">Número</label>
                  <input
                    id="np-num"
                    type="text"
                    placeholder="Nº"
                    value={addr.numero || ''}
                    onChange={(e) => setAddr('numero', e.target.value)}
                  />
                </div>
                <div className="novo-protocolo-field">
                  <label htmlFor="np-bairro">Bairro</label>
                  <input
                    id="np-bairro"
                    type="text"
                    placeholder="Bairro"
                    value={addr.bairro || ''}
                    onChange={(e) => setAddr('bairro', e.target.value)}
                  />
                </div>
                <div className="novo-protocolo-field">
                  <label htmlFor="np-cep">CEP</label>
                  <input
                    id="np-cep"
                    type="text"
                    placeholder="00000-000"
                    value={addr.cep || ''}
                    onChange={(e) => setAddr('cep', e.target.value)}
                    inputMode="numeric"
                  />
                </div>
                <div className="novo-protocolo-field">
                  <label htmlFor="np-cidade">Cidade / UF</label>
                  <input
                    id="np-cidade"
                    type="text"
                    placeholder="Ex.: São Paulo - SP"
                    value={addr.cidade_uf || ''}
                    onChange={(e) => setAddr('cidade_uf', e.target.value)}
                  />
                </div>
              </div>
            </section>

            <section className="novo-protocolo-section" aria-labelledby="np-sec-area">
              <h3 className="novo-protocolo-section-title" id="np-sec-area">
                Áreas (m²)
              </h3>
              <div className="novo-protocolo-grid novo-protocolo-grid--2">
                <div className="novo-protocolo-field">
                  <label htmlFor="np-ater">Área do terreno</label>
                  <input
                    id="np-ater"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0"
                    value={form.area_terreno}
                    onChange={(e) => setForm({ ...form, area_terreno: e.target.value })}
                  />
                </div>
                <div className="novo-protocolo-field">
                  <label htmlFor="np-aconstr">Área construída</label>
                  <input
                    id="np-aconstr"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0"
                    value={form.area_construida}
                    onChange={(e) => setForm({ ...form, area_construida: e.target.value })}
                  />
                </div>
              </div>
            </section>

            <section className="novo-protocolo-section" aria-labelledby="np-sec-desc">
              <h3 className="novo-protocolo-section-title" id="np-sec-desc">
                Descrição
              </h3>
              <div className="novo-protocolo-field">
                <label htmlFor="np-desc">Resumo da obra ou solicitação</label>
                <textarea
                  id="np-desc"
                  placeholder="Descreva de forma objetiva a intervenção, alvará pretendido e outras informações relevantes."
                  value={form.descricao}
                  onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                />
              </div>
            </section>

            <div className="novo-protocolo-actions">
              <p className="novo-protocolo-hint">Depois de criar, poderá anexar documentos na ficha do protocolo.</p>
              <div className="novo-protocolo-actions-btns">
                <button type="button" className="sispro-btn sispro-btn--primary" onClick={() => mutation.mutate()} disabled={mutation.isLoading}>
                  {mutation.isLoading ? 'Criando…' : 'Criar protocolo'}
                </button>
                <Link to={routes.protocolos} className="sispro-btn sispro-btn--secondary">
                  Cancelar
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
